import { FC, useCallback, useState } from 'react';
import { ArrowBackIos } from '@mui/icons-material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { IconButton, Radio, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { CardActions } from './CardActions';
import { CardDeleteConfirmActions } from './CardDeleteConfirmActions';

import { FEATURE_FLAGS, PAYMENT_STEP, QUERY_KEY } from '~common';
import {
  MethodItem,
  PaymentMethodItem,
  WalletPaymentMethodItem,
} from '~components/atoms/new-step-three/PaymentMethodItem';
import { LocalizationTypography } from '~components/organisms/localization';
import { useMutationDeletePaymentMethod } from '~hooks';
import {
  isSelectedCampusCard,
  usePaymentStepContext,
  useStationInformationContext,
  useUserPaymentContext,
} from '~providers';
import { PaymentInfo } from '~types';
import { checkEnabledFeatureFlag } from '~utils';

export const SelectPaymentMethod: FC = () => {
  const { paymentMethods } = useUserPaymentContext();
  const queryClient = useQueryClient();
  const [isDeleteMode, setIsDeleteMode] = useState(false);

  const isMultiCardsPerUserEnabled = checkEnabledFeatureFlag(
    FEATURE_FLAGS.MULTI_CARDS_PER_USER_BY_WEB_PLATFORM,
  );

  // Every saved Stripe card is listed, including the ones created through a
  // wallet flow (method APPLE_PAY/GOOGLE_PAY) — those are real cards too. The
  // wallet row below is a separate always-present entry and never a duplicate
  // of these.
  // Multi-card mode lists every saved card; otherwise keep the single-card
  // display (the default method — the first one, ordered by last-rented)
  // unchanged.
  const payments = isMultiCardsPerUserEnabled
    ? paymentMethods
    : paymentMethods.slice(0, 1);

  const { campusCard } = useStationInformationContext();
  const isCampusCardEnabled =
    checkEnabledFeatureFlag(FEATURE_FLAGS.CAMPUS_CARD_BY_WEB_PLATFORM) &&
    campusCard.enabled;

  const {
    handleSetPaymentStep,
    selectedPaymentMethod,
    handleSetSelectedPaymentMethod,
    selectedPaymentMethodToDelete,
    handleSetSelectedPaymentMethodToDelete,
  } = usePaymentStepContext();
  const selectedCard =
    selectedPaymentMethod &&
    selectedPaymentMethod !== 'WALLET' &&
    !isSelectedCampusCard(selectedPaymentMethod)
      ? selectedPaymentMethod
      : null;

  const {
    mutateAsync: deletePaymentMethod,
    isLoading: isDeletingPaymentMethod,
  } = useMutationDeletePaymentMethod({
    onErrorCallback: () => {
      handleSetSelectedPaymentMethodToDelete(null);
    },
  });

  const handleSetupOrSelectPaymentMethod = useCallback(
    (paymentMethod: PaymentInfo | null) => {
      if (paymentMethod) {
        handleSetSelectedPaymentMethod(paymentMethod);
      } else {
        handleSetPaymentStep(PAYMENT_STEP.ADD_PAYMENT_METHOD);
      }
    },
    [handleSetSelectedPaymentMethod, handleSetPaymentStep],
  );

  const handleEnterDeleteMode = useCallback(() => {
    setIsDeleteMode(true);
    handleSetSelectedPaymentMethodToDelete(null);
  }, [handleSetSelectedPaymentMethodToDelete]);

  const handleCancelDeleteMode = useCallback(() => {
    setIsDeleteMode(false);
    handleSetSelectedPaymentMethodToDelete(null);
  }, [handleSetSelectedPaymentMethodToDelete]);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedPaymentMethodToDelete) {
      return;
    }

    try {
      await deletePaymentMethod({ id: selectedPaymentMethodToDelete.id });
    } catch {
      // The mutation has already toasted why (e.g. the card funds a held pass)
      // and cleared the selection. The card still exists, so nothing below
      // applies — and letting the rejection escape only logs it as uncaught.
      return;
    }

    if (selectedCard?.id === selectedPaymentMethodToDelete.id) {
      handleSetSelectedPaymentMethod('WALLET');
    }

    handleSetSelectedPaymentMethodToDelete(null);
    setIsDeleteMode(false);
    await queryClient.invalidateQueries([QUERY_KEY.GET_PAYMENT_INFO]);
    await queryClient.invalidateQueries([QUERY_KEY.GET_USER_PAYMENT_METHODS]);
  }, [
    deletePaymentMethod,
    handleSetSelectedPaymentMethod,
    handleSetSelectedPaymentMethodToDelete,
    queryClient,
    selectedCard,
    selectedPaymentMethodToDelete,
  ]);

  return (
    <>
      <Stack direction="row">
        {/* If there is a selected payment method, show the back button */}
        {!!selectedPaymentMethod && (
          <IconButton
            aria-label="back"
            size="small"
            color="inherit"
            sx={{ paddingY: 0 }}
            onClick={() => handleSetPaymentStep(PAYMENT_STEP.RENT_A_CHARGER)}
          >
            <ArrowBackIos />
          </IconButton>
        )}
        <LocalizationTypography
          textId="app.rentalPage.newStepThree.selectPaymentMethod.title"
          defaultText="Payment Method"
          variant="Poppins/Heading-3"
        />
      </Stack>

      {/* PAYMENT METHOD SELECTION */}
      <Stack direction="column">
        <WalletPaymentMethodItem
          action={
            <Radio
              checked={!isDeleteMode && selectedPaymentMethod === 'WALLET'}
              value="WALLET"
              name="radio-buttons"
              inputProps={{ 'aria-label': 'WALLET' }}
              disabled={isDeleteMode}
            />
          }
          onClick={
            isDeleteMode
              ? undefined
              : () => handleSetSelectedPaymentMethod('WALLET')
          }
        />

        {payments.length > 0 ? (
          payments.map((payment) => (
            <PaymentMethodItem
              key={payment.id}
              enableDivider={false}
              card={payment}
              action={
                <Radio
                  checked={
                    isDeleteMode
                      ? selectedPaymentMethodToDelete?.id === payment.id
                      : selectedCard?.id === payment.id
                  }
                  value={payment}
                  name="radio-buttons"
                  inputProps={{ 'aria-label': 'CARD' }}
                />
              }
              onClick={() =>
                isDeleteMode
                  ? handleSetSelectedPaymentMethodToDelete(payment)
                  : handleSetupOrSelectPaymentMethod(payment)
              }
            />
          ))
        ) : (
          <PaymentMethodItem
            enableDivider={false}
            card={undefined}
            action={
              <Radio
                checked={selectedPaymentMethod === null}
                value={null}
                name="radio-buttons"
                inputProps={{ 'aria-label': 'CARD' }}
              />
            }
            onClick={() => handleSetupOrSelectPaymentMethod(null)}
          />
        )}

        {/* PAYMENT METHOD ACTIONS */}
        {payments.length > 0 && (
          <>
            {isDeleteMode ? (
              <CardDeleteConfirmActions
                onCancel={handleCancelDeleteMode}
                onConfirm={handleConfirmDelete}
                isDeleting={isDeletingPaymentMethod}
                canConfirm={!!selectedPaymentMethodToDelete}
              />
            ) : (
              <CardActions
                onAdd={() =>
                  handleSetPaymentStep(PAYMENT_STEP.ADD_PAYMENT_METHOD)
                }
                onDelete={handleEnterDeleteMode}
                showDelete={isMultiCardsPerUserEnabled}
              />
            )}
          </>
        )}

        {/* CAMPUS CARD ENTRY (feature-flagged + station has campus card enabled) */}
        {isCampusCardEnabled && !isDeleteMode && (
          <MethodItem
            enableDivider={false}
            icon={
              <SchoolOutlinedIcon
                sx={{
                  color: (theme) => theme.palette.text.secondary,
                  fontSize: 30,
                }}
              />
            }
            title={
              <Stack direction="column">
                <LocalizationTypography
                  textId="app.rentalPage.newStepThree.selectPaymentMethod.studentCampusCard"
                  defaultText="Student Campus Card"
                  variant="Poppins/Subheading-md"
                  color="#111928"
                />
                <LocalizationTypography
                  textId="app.rentalPage.newStepThree.selectPaymentMethod.tapToLinkSchool"
                  defaultText="Tap to link your school"
                  variant="Inter/Body-sm"
                  color="#6B7280"
                />
              </Stack>
            }
            action={
              <Radio
                checked={isSelectedCampusCard(selectedPaymentMethod)}
                value="CAMPUS_CARD"
                name="radio-buttons"
                inputProps={{ 'aria-label': 'CAMPUS_CARD' }}
              />
            }
            onClick={() => handleSetPaymentStep(PAYMENT_STEP.CAMPUS_CARD_LIST)}
          />
        )}
      </Stack>
    </>
  );
};
